import useEmailTemplatesSelect from '../../../hooks/useEmailTemplatesSelect';
import React, { useState } from 'react';
import { GenericDropdownOption } from 'App.types';
import styles from '../OutreachInitialEmailPreview/OutreachInitialEmailPreview.module.css';
import { Banner, Button, Select } from '@round/ui-kit';
import cn from 'classnames';
import { notificationTypeLabels } from '../../helpers';
import EmailContent from '../EmailContent/EmailContent';
import { useEmailPreview } from 'Modules/Advertising/Microwave/components/EmailPreview/useEmailPreview';
import { useSendInitialEmails } from 'Modules/Advertising/Microwave/components/EmailPreview/hooks/useSendEmails';

export type PreviewCreator = {
    influencer_id: number;
    nickname: string;
    email: string;
};

type Props = {
    campaignId: number;
    creators: PreviewCreator[];
    onEmailSent: (influencerIds: number[]) => void;
};

const CreatorsInitialEmailPreview = ({ campaignId, creators, onEmailSent }: Props) => {
    const {
        status: templatesStatus,
        templateOptions,
        error: templatesError,
        selectedTemplate,
        setSelectedTemplate,
    } = useEmailTemplatesSelect('SONG_PROMO_INITIAL_REGISTERED');

    const [selectedCreator, setSelectedCreator] = useState<GenericDropdownOption<number>>({
        value: creators[0]?.influencer_id,
        label: creators[0]?.nickname,
    });

    const [emailData, setEmailData] = useState<PreviewCreator[]>(creators);

    const {
        data: emailPreviewData,
        status: emailPreviewStatus,
        error: emailPreviewError,
        reset: resetEmailPreview,
    } = useEmailPreview({
        params: {
            campaign_id: campaignId,
            email_template_id: selectedTemplate?.value!,
            influencer_id: selectedCreator?.value!,
        },
        isDisabled: !selectedTemplate?.value || !selectedCreator?.value,
    });

    const { send, data: sendResult, error: sendError, status: sendStatus } = useSendInitialEmails();
    const successfulCount = sendResult?.success.length ?? 0;
    const failedCount = sendResult?.failure.length ?? 0;

    const handleSend = async () => {
        if (!selectedTemplate?.value) {
            return;
        }

        const result = await send(
            campaignId,
            emailData.map((previewUser) => ({
                influencer_id: previewUser.influencer_id,
                email_address: previewUser.email,
                email_template_id: selectedTemplate.value,
            }))
        ).catch(() => {});

        if (result && result.success) {
            onEmailSent?.(result.data.success);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                {creators.length > 1 ? (
                    <div className={styles.control}>
                        <label className={styles.controlLabel}>Creator</label>
                        <Select
                            isClearable={false}
                            value={selectedCreator}
                            onChange={(creator) => {
                                setSelectedCreator(creator!);
                                resetEmailPreview();
                            }}
                            options={creators.map((c) => ({ value: c.influencer_id, label: c.nickname }))}
                        />
                    </div>
                ) : null}

                <div className={styles.control}>
                    <label className={styles.controlLabel}>Email</label>
                    <input
                        className={styles.emailInput}
                        value={emailData.find((d) => d.influencer_id === selectedCreator.value)?.email}
                        onChange={(e) => {
                            setEmailData((emailData) =>
                                emailData.map((item) => {
                                    if (item.influencer_id === selectedCreator.value) {
                                        return { ...item, email: e.target.value };
                                    }

                                    return item;
                                })
                            );
                        }}
                    />
                </div>

                <div className={cn(styles.control, styles.templateControl)}>
                    <label className={styles.controlLabel}>
                        {notificationTypeLabels['SONG_PROMO_INITIAL_REGISTERED']} Templates
                    </label>
                    <Select
                        isLoading={templatesStatus === 'not-initialized'}
                        options={templateOptions}
                        value={selectedTemplate}
                        onChange={(value) => {
                            if (value) {
                                setSelectedTemplate(value);
                                resetEmailPreview();
                            }
                        }}
                        noOptionsMessage={() => templatesError || 'No options'}
                    />
                </div>
            </div>

            <EmailContent
                isLoading={emailPreviewStatus === 'idle' || emailPreviewStatus === 'loading'}
                error={emailPreviewError ? [emailPreviewError] : []}
                preview={emailPreviewData}
            />

            {sendStatus === 'error' && (
                <Banner appearance="danger" className={styles.banner}>
                    {sendError}
                </Banner>
            )}
            {sendStatus === 'success' && (
                <Banner appearance="info" className={styles.banner}>
                    <span>Success: {successfulCount}</span>
                    &nbsp;
                    <span>Failed: {failedCount}</span>
                </Banner>
            )}

            {sendStatus !== 'success' && (
                <Button
                    type="filled"
                    color="black"
                    loading={sendStatus === 'loading'}
                    disabled={
                        sendStatus === 'loading' ||
                        !selectedTemplate?.value ||
                        //Though it's not necessary for sending emails, we want to prevent sending if the email preview is not available
                        emailPreviewStatus !== 'success'
                    }
                    onClick={handleSend}
                >
                    Send
                </Button>
            )}
        </div>
    );
};

export default CreatorsInitialEmailPreview;
